import { Suspense, lazy, useState, useEffect, useRef } from 'react'
import Overlays from './Overlays'
import EndCard from './EndCard'
import Backgrounds from './Backgrounds'
import { scrollState } from './scrollState'

// Lazy-load the entire 3D layer (Canvas + Scene + R3F + three.js + drei + GLB
// loader + Particles) so the initial bundle is text+images only. The 3D chunk
// loads in parallel while the user reads Beats 1–3; by the time they reach
// Beat 4 (~t=0.43) it's ready. Importing Canvas itself out of App.jsx is what
// actually moves three.js out of the main bundle.
const Canvas3D = lazy(() => import('./Canvas3D'))

export default function App() {
  const [ready, setReady] = useState(false)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const [started, setStarted] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  // Conditional Canvas mount: Canvas3D is null until the user scrolls toward
  // Beat 4. Before that, no continuous 3D rendering = no main-thread blocking.
  // We pre-warm the lazy chunk earlier (at t > 0.10) so by the time we mount,
  // the JS is in cache and mount is instant.
  const [show3D, setShow3D] = useState(false)
  const show3DRef = useRef(false)
  const prefetchedRef = useRef(false)

  useEffect(() => {
    const update = () => {
      setSize({ w: window.innerWidth, h: window.innerHeight })
      setIsMobile(window.matchMedia('(max-width: 768px)').matches)
      setReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    }
    update()
    window.addEventListener('resize', update)
    setReady(true)
    return () => window.removeEventListener('resize', update)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const t = scrollState.offset
      // Warm the lazy chunk cache as the user moves past Beat 1.
      if (!prefetchedRef.current && t > 0.10) {
        prefetchedRef.current = true
        import('./Canvas3D')
      }
      // Mount the Canvas just before Beat 4's promise window starts.
      if (!show3DRef.current && t > 0.35) {
        show3DRef.current = true
        setShow3D(true)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  if (!ready) return null
  if (isMobile && !started) return <MobileGate onStart={() => setStarted(true)} />
  if (reducedMotion) return <StaticFallback />

  return (
    <>
      <Backgrounds isMobile={isMobile} />

      <div style={{ position: 'fixed', inset: 0, zIndex: 2, width: size.w, height: size.h, pointerEvents: 'none' }}>
        {show3D && (
          <Suspense fallback={null}>
            <Canvas3D size={size} isMobile={isMobile} />
          </Suspense>
        )}
      </div>

      <div style={{ position: 'relative', zIndex: 3 }}>
        <Overlays isMobile={isMobile} />
      </div>

      <EndCard />

      <BottomBar />
    </>
  )
}

// Bottom bar — tri-link CTA in the Scroll-hint typography. Reuses the same
// font, letter-spacing and colour as the original scroll hint so it reads as
// part of the cinematic vocabulary rather than UI chrome. Scroll-hint fades
// out early (t > 0.55) once the user is past Beat 1; KNOW MORE + TALK TO US
// persist until the EndCard centres in (t > 0.92).
function BottomBar() {
  const [, force] = useState(0)
  useEffect(() => {
    let raf
    const loop = () => {
      force((n) => (n + 1) & 0xff)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [])
  const t = scrollState.offset
  const scrollVisible = Math.max(0, 1 - Math.max(0, (t - 0.55)) * 4)
  const linksVisible = Math.max(0, 1 - Math.max(0, (t - 0.90)) * 25)
  if (linksVisible <= 0.01) return null

  const linkStyle = {
    pointerEvents: 'auto',
    fontSize: '0.74rem',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.7)',
    textDecoration: 'none',
    padding: '0.5rem 0.25rem',
    whiteSpace: 'nowrap',
  }
  const scrollHintStyle = {
    fontSize: '0.74rem',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.55)',
    animation: 'pulse 2.5s ease-in-out infinite',
    pointerEvents: 'none',
    opacity: scrollVisible,
    whiteSpace: 'nowrap',
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0 1rem',
        pointerEvents: 'none',
        zIndex: 5,
        opacity: linksVisible,
        transition: 'opacity 0.15s linear',
      }}
    >
      <a
        href="https://aqi0.com/air-purifier/"
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
      >
        Know more →
      </a>
      {scrollVisible > 0.01 ? (
        <div style={scrollHintStyle}>Scroll ↓</div>
      ) : null}
      <a
        href="https://wa.me/919667672740?text=Hi%20aqi0%2C%20I%20saw%20the%20%2Fair%2F%20page%20-%20can%20we%20talk%3F"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Talk to us on WhatsApp"
        style={linkStyle}
      >
        Talk to us →
      </a>
    </div>
  )
}

function MobileGate({ onStart }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex',
      flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      padding: '2rem', color: '#fff', textAlign: 'center',
      background: '#0a0a0a',
      overflow: 'hidden',
    }}>
      {/* Faded backdrop hint of Beat 1 — gives context to the splash */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url(${import.meta.env.BASE_URL}bg-beat1.webp?v=1)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.28,
        filter: 'saturate(0.7) brightness(0.6)',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.85) 100%)',
      }} />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ fontSize: '0.78rem', letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.6 }}>aqi0</div>
        <h1 style={{ fontSize: '2.4rem', margin: '1rem 0 0', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.05 }}>
          The whole-home<br />air purifier.
        </h1>
        <p style={{ opacity: 0.8, marginTop: '1.5rem', maxWidth: '32ch', lineHeight: 1.5 }}>
          Why indoor air purifiers can't fix polluted air, and what does. A 3-minute scroll.
        </p>
        <button onClick={onStart} style={{
          marginTop: '2.5rem', padding: '0.95rem 2rem',
          background: '#8AB63F', color: '#fff', border: 'none', borderRadius: '999px',
          fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 8px 28px rgba(138,182,63,0.35)',
        }}>Tap to begin →</button>
        <p style={{ opacity: 0.45, marginTop: '2rem', fontSize: '0.78rem' }}>Uses 3D graphics · works best on Wi-Fi</p>
      </div>
    </div>
  )
}

function StaticFallback() {
  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex',
      flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      padding: '2rem', textAlign: 'center', color: '#222',
    }}>
      <h1 style={{ fontSize: '2rem', margin: 0, fontWeight: 600 }}>The whole-home air purifier.</h1>
      <p style={{ opacity: 0.6, marginTop: '1rem', maxWidth: '38ch' }}>
        aqi0 pulls outdoor air through H13 HEPA filtration into your home, pressurises the space, and pushes stale air out through natural gaps.
      </p>
    </div>
  )
}
